/**
 * @module features/display-module/components/DdmNotificationTable
 * @description Responsive Display Artifact list rendering a semantic data table on
 * Tablet/Desktop (`>=640px`) and touch-optimized stacked cards on Mobile (`<640px`) (`T032`).
 */

import * as React from "react";
import {
  Pencil,
  Trash2,
  Image as ImageIcon,
  Video as VideoIcon,
  Images,
  MonitorSmartphone,
  Calendar,
  Timer,
  Pause,
  Play,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DdmNotificationRecord } from "../types/ddm";
import { DdmArtifactTypeBadge, DdmStatusBadge } from "./DdmStatusBadge";

interface DdmNotificationTableProps {
  items: DdmNotificationRecord[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  onPageChange: (page: number) => void;
  onEdit: (record: DdmNotificationRecord) => void;
  onDelete: (record: DdmNotificationRecord) => void;
  onToggleEmergencyPause?: (record: DdmNotificationRecord) => void;
  isUpdatingId?: string | null;
}

function formatDateTimeShort(iso: string): string {
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function renderMediaPill(record: DdmNotificationRecord) {
  const cp = record.content_payload ?? {};
  if (cp.video_url) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/10 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 text-[10px] font-medium">
        <VideoIcon className="h-3 w-3" />
        <span>
          Video
          {cp.video_duration_seconds ? ` (${cp.video_duration_seconds}s)` : ""}
        </span>
      </span>
    );
  }
  if (Array.isArray(cp.image_urls) && cp.image_urls.length > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 text-[10px] font-medium">
        <Images className="h-3 w-3" />
        <span>{cp.image_urls.length} Photos</span>
      </span>
    );
  }
  if (cp.image_url) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-sky-500/10 text-sky-700 dark:text-sky-300 px-1.5 py-0.5 text-[10px] font-medium">
        <ImageIcon className="h-3 w-3" />
        <span>Image</span>
      </span>
    );
  }
  return null;
}

export const DdmNotificationTable: React.FC<DdmNotificationTableProps> =
  React.memo(
    ({
      items,
      pagination,
      onPageChange,
      onEdit,
      onDelete,
      onToggleEmergencyPause,
      isUpdatingId,
    }) => {
      return (
        <div className="space-y-4">
          {/* Desktop & Tablet Table View (>=640px) */}
          <div className="hidden sm:block rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/90 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border/60 dark:border-zinc-800/80 bg-muted/30 dark:bg-zinc-900/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="py-3.5 px-5">Artifact & Content</th>
                    <th className="py-3.5 px-4">Type & Media</th>
                    <th className="py-3.5 px-4">Target DDM Device</th>
                    <th className="py-3.5 px-4">Schedule & Cycle</th>
                    <th className="py-3.5 px-4">Device Sync Status</th>
                    <th className="py-3.5 px-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50 dark:divide-zinc-800/60 text-xs">
                  {items.map((record) => {
                    const cp = record.content_payload ?? {};
                    const subtitle =
                      cp.body ||
                      (record.artifact_type === "event" && record.event_date
                        ? `Event Date: ${record.event_date}`
                        : record.remarks || "Display artifact");

                    return (
                      <tr
                        key={record.id}
                        className="group hover:bg-muted/30 dark:hover:bg-zinc-900/40 transition-colors"
                      >
                        {/* Title & Snippet */}
                        <td className="py-3.5 px-5 max-w-[260px]">
                          <div className="font-semibold text-foreground truncate">
                            {record.search_title || "Untitled Artifact"}
                          </div>
                          <div className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                            {subtitle}
                          </div>
                        </td>

                        {/* Type & Media */}
                        <td className="py-3.5 px-4">
                          <div className="flex flex-col items-start gap-1.5">
                            <DdmArtifactTypeBadge type={record.artifact_type} />
                            {renderMediaPill(record)}
                          </div>
                        </td>

                        {/* Device */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1.5 font-medium text-foreground">
                            <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate max-w-[160px]">
                              {record.device_name}
                            </span>
                          </div>
                          <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                            {record.registered_device_id}
                          </div>
                        </td>

                        {/* Schedule & Cycle */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1 text-[11px] text-foreground">
                            <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span>
                              {formatDateTimeShort(record.start_timestamp)} →{" "}
                              {formatDateTimeShort(record.end_timestamp)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
                            <Timer className="h-3 w-3 shrink-0" />
                            {record.artifact_type === "emergency_alert" ? (
                              <span>Persistent Override Alert</span>
                            ) : (
                              <span>
                                {record.duration_of_display ?? 15}s ·{" "}
                                {record.per_cycle_display_count}x / cycle
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Sync Status */}
                        <td className="py-3.5 px-4">
                          <DdmStatusBadge
                            status={record.delivery_status}
                            version={record.version}
                            deviceReceived={record.device_received}
                            isLive={record.is_live}
                          />
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-5 text-right">
                          <div className="inline-flex items-center justify-end gap-1.5">
                            {record.artifact_type === "emergency_alert" &&
                              onToggleEmergencyPause && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isUpdatingId === record.id}
                                  onClick={() => onToggleEmergencyPause(record)}
                                  className="h-8 px-2.5 text-[11px] rounded-lg gap-1"
                                  title={
                                    record.pause_emergency_alert
                                      ? "Resume emergency alert"
                                      : "Pause emergency alert"
                                  }
                                >
                                  {record.pause_emergency_alert ? (
                                    <>
                                      <Play className="h-3 w-3 text-emerald-500" />
                                      <span>Resume</span>
                                    </>
                                  ) : (
                                    <>
                                      <Pause className="h-3 w-3 text-amber-500" />
                                      <span>Pause</span>
                                    </>
                                  )}
                                </Button>
                              )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(record)}
                              aria-label={`Edit ${record.search_title}`}
                              className="h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(record)}
                              aria-label={`Delete ${record.search_title}`}
                              className="h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Stacked Card View (<640px) with >=44px touch targets */}
          <div className="sm:hidden space-y-3">
            {items.map((record) => {
              const cp = record.content_payload ?? {};
              const subtitle =
                cp.body ||
                (record.artifact_type === "event" && record.event_date
                  ? `Event Date: ${record.event_date}`
                  : record.remarks || "Display artifact");

              return (
                <article
                  key={record.id}
                  className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/90 p-4 space-y-3 shadow-xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <DdmArtifactTypeBadge type={record.artifact_type} />
                        {renderMediaPill(record)}
                      </div>
                      <h3 className="text-sm font-semibold text-foreground mt-2 truncate">
                        {record.search_title || "Untitled Artifact"}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {subtitle}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-1.5 text-[11px] text-muted-foreground bg-muted/30 dark:bg-zinc-900/50 rounded-xl p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground truncate">
                        <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{record.device_name}</span>
                      </span>
                      <span className="font-mono text-[10px]">
                        {record.registered_device_id}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">
                        {formatDateTimeShort(record.start_timestamp)} →{" "}
                        {formatDateTimeShort(record.end_timestamp)}
                      </span>
                      <span className="shrink-0 font-medium">
                        {record.artifact_type === "emergency_alert"
                          ? "Persistent"
                          : `${record.duration_of_display ?? 15}s · ${record.per_cycle_display_count}x`}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <DdmStatusBadge
                      status={record.delivery_status}
                      version={record.version}
                      deviceReceived={record.device_received}
                      isLive={record.is_live}
                    />

                    <div className="flex items-center gap-1.5">
                      {record.artifact_type === "emergency_alert" &&
                        onToggleEmergencyPause && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isUpdatingId === record.id}
                            onClick={() => onToggleEmergencyPause(record)}
                            className="min-h-[44px] px-3 rounded-xl text-xs gap-1"
                          >
                            {record.pause_emergency_alert ? (
                              <>
                                <Play className="h-3.5 w-3.5 text-emerald-500" />
                                <span>Resume</span>
                              </>
                            ) : (
                              <>
                                <Pause className="h-3.5 w-3.5 text-amber-500" />
                                <span>Pause</span>
                              </>
                            )}
                          </Button>
                        )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(record)}
                        aria-label={`Edit ${record.search_title}`}
                        className="min-h-[44px] min-w-[44px] p-0 rounded-xl"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(record)}
                        aria-label={`Delete ${record.search_title}`}
                        className="min-h-[44px] min-w-[44px] p-0 rounded-xl text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pagination Bar */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-2 py-2 text-xs text-muted-foreground">
              <span>
                Page{" "}
                <strong className="text-foreground">{pagination.page}</strong> of{" "}
                <strong className="text-foreground">
                  {pagination.total_pages}
                </strong>{" "}
                ({pagination.total_items} artifacts)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!pagination.has_previous}
                  onClick={() => onPageChange(pagination.page - 1)}
                  className="h-9 px-3 rounded-xl gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Previous</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!pagination.has_next}
                  onClick={() => onPageChange(pagination.page + 1)}
                  className="h-9 px-3 rounded-xl gap-1"
                >
                  <span>Next</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }
  );

DdmNotificationTable.displayName = "DdmNotificationTable";

